/**
 * StylistFlow — Lunora's white-label AI Body Scan flow.
 *
 * STRICT linear customer journey:
 *   welcome → scan → fitPref → review
 *
 * The scan step launches the Lunora AI Body Scan (powered by an underlying
 * provider — never named in the customer UI). The scanned body values ARE
 * the authoritative height / weight / measurements. Fit preference is chosen
 * AFTER the scan, and the Fit Fusion Engine adds ease on top of the real body.
 *
 * On mount, this file runs a 3-layer post-scan recovery:
 *   A. URL params  — provider returnUrl carries ?id / ?uuid
 *   B. Saved active profile — GET /api/body-profile/me short-circuit
 *   C. Scan-session token   — recover a pending scan via person listing
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { measurementsAPI, bodyProfileAPI } from '../api';
import { resolveOrderContext } from '../data/measureContext';
import { ingestWithRetry } from '../utils/scanIngestRetry';
import {
  Sparkles, ChevronLeft, ArrowRight, Camera, Ruler, Scale,
  Wand2, ShieldCheck, Loader2,
} from 'lucide-react';
import EditableReview from './EditableReview';
import StartBodyScanButton from './StartBodyScanButton';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const QUICKSTART_KEY = 'lunora_stylist_quickstart';

// ─── Schema mapping (which measurement schema does the chosen category use) ──
const CATEGORY_TO_SCHEMA = {
  blouse: 'blouse', saree_blouse: 'blouse',
  suit: 'suit', salwar_suit: 'suit', anarkali: 'suit', kurti: 'suit', lehenga: 'suit',
  kurta: 'kurta_pajama', kurta_pajama: 'kurta_pajama',
  shirt: 'men_shirt', men_shirt: 'men_shirt',
  trouser: 'trouser', pant: 'trouser', jeans: 'trouser',
  sherwani: 'sherwani', blazer: 'sherwani', suit_men: 'sherwani',
};

const SCHEMA_LABELS = {
  blouse: 'Blouse', suit: 'Ladies Suit', kurta_pajama: 'Kurta Pajama',
  men_shirt: 'Men Shirt', trouser: 'Trouser', sherwani: 'Sherwani / Blazer',
};

// Sensible default style answers per schema (we no longer ask these in the
// measurement flow — DesignStudio collects them earlier in the journey).
const DEFAULT_ANSWERS = {
  blouse: { sleeve_style: 'short', neckline_depth: 'regular' },
  suit: { kurti_length_pref: 'knee', bottom_style: 'salwar' },
  kurta_pajama: { kurta_length_pref: 'regular', sleeve_style: 'full' },
  men_shirt: { sleeve: 'full' },
  trouser: { leg_style: 'straight', length_pref: 'regular' },
  sherwani: { length_pref: 'knee' },
};

const SIZE_CHIPS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

// Schemas where the "Padded / Non-Padded" toggle should appear (women's upper-body
// garments where bust shaping, cup allowance, and dart behavior depend on padding).
const PADDED_SCHEMAS = new Set(['blouse', 'suit']);
// Treat all of these category strings as "blouse-like" upper-body for the toggle:
//   blouse, saree_blouse, lehenga blouse pieces, suit (anarkali, kurti, salwar_suit, gown, crop blouse)
// All map to schema 'blouse' or 'suit', so the schema check above is sufficient.

const AGE_RANGES = [
  { v: 'under_25', l: 'Under 25' },
  { v: '25_35',    l: '25 – 35' },
  { v: '35_50',    l: '35 – 50' },
  { v: 'over_50',  l: '50+' },
];

// 4 luxury fit options — mapped to backend ease constants.
// Backend accepts these as fit_pref: slim, regular, relaxed, luxury_loose.
const FIT_OPTIONS = [
  { ui: 'slim',         backend: 'slim',         label: 'Slim Fit',     desc: 'Closer to the body with minimal ease' },
  { ui: 'regular',      backend: 'regular',      label: 'Regular Fit',  desc: 'Balanced ease for a tailored silhouette' },
  { ui: 'relaxed',      backend: 'relaxed',      label: 'Relaxed Fit',  desc: 'More room for comfortable movement' },
  { ui: 'luxury_loose', backend: 'luxury_loose', label: 'Luxury Loose', desc: 'Generous ease for a draped silhouette' },
];

// ─── Linear step definitions ─────────────────────────────────────────────────
// New customer flow: welcome → scan → fitPref → review.
// The AI Body Scan provides authoritative height/weight/measurements so we no
// longer ask the customer for height/weight up front.
const TOTAL_VISIBLE_STEPS = 2; // Scan, Fit Preference (Welcome + Review are transitions)

// ─── Reusable header / footer ────────────────────────────────────────────────
const StepHeader = ({ stepNumber, title, sub, onBack, schemaLabel }) => (
  <div className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: LINE }}>
    <div className="flex items-center justify-between px-4 py-3">
      <button data-testid="step-back-btn" onClick={onBack} className="p-1.5 -ml-1.5">
        <ChevronLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
      </button>
      <div className="text-center flex-1">
        <p className="text-[9px] tracking-[0.22em] uppercase" style={{ color: GOLD }}>
          Step {stepNumber} of {TOTAL_VISIBLE_STEPS}
        </p>
        <p className="text-[15px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
          {title}
        </p>
        {sub && <p className="text-[9px]" style={{ color: MUTED }}>{sub}</p>}
        {schemaLabel && !sub && <p className="text-[9px]" style={{ color: MUTED }}>{schemaLabel}</p>}
      </div>
      <div className="w-9" />
    </div>
    <div className="h-0.5 bg-gray-100">
      <div className="h-full transition-all duration-500"
        style={{ width: `${(stepNumber / TOTAL_VISIBLE_STEPS) * 100}%`, backgroundColor: GOLD }} />
    </div>
  </div>
);

const StepFooter = ({ onClick, disabled, testId, children }) => (
  <div className="sticky bottom-0 bg-white border-t" style={{ borderColor: LINE }}>
    <div className="px-4 py-3">
      <button
        data-testid={testId} onClick={onClick} disabled={disabled}
        className="w-full py-3.5 text-[12px] tracking-[0.18em] uppercase text-white flex items-center justify-center gap-2 disabled:opacity-40"
        style={{
          background: disabled
            ? 'linear-gradient(135deg, #9A9A9A, #C0C0C0)'
            : 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)',
          fontWeight: 500,
          transition: 'background 280ms ease',
        }}>
        {children}
      </button>
    </div>
  </div>
);

// ─── Step 0 — Welcome / Hero ─────────────────────────────────────────────────
const WelcomeHero = ({ schemaLabel, userName, onContinue }) => (
  <div data-testid="stylist-welcome" className="min-h-screen flex flex-col"
    style={{
      background: `radial-gradient(circle at 30% 0%, rgba(212,175,55,0.18) 0%, transparent 55%), ${SOFT}`,
    }}>
    <div className="flex-1 flex flex-col px-6 pt-14 pb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-px w-8" style={{ background: GOLD }} />
        <p className="text-[9px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>Lunora · AI Stylist</p>
      </div>

      <h1 className="leading-[1.05] mb-3"
        style={{
          fontFamily: 'Playfair Display, serif',
          color: INK,
          fontSize: 'clamp(34px, 9vw, 44px)',
          fontWeight: 500,
          letterSpacing: '-0.015em',
        }}>
        {userName ? `${userName.split(' ')[0]}, your` : 'Your'}<br />
        <span style={{ fontStyle: 'italic', color: GOLD }}>AI Stylist</span> is ready
      </h1>
      <p className="text-[13px] mb-8" style={{ color: MUTED, lineHeight: 1.6 }}>
        Launch your Lunora&apos;s AI Body Scan for a couture-grade fit.
        We craft your perfect <b style={{ color: INK }}>{schemaLabel}</b> in seconds.
      </p>

      {/* Mini journey preview */}
      <div className="mb-10 space-y-2">
        {[
          { Icon: Camera,   label: 'AI Body Scan',        sub: 'Under a minute · fully guided' },
          { Icon: Ruler,    label: 'Choose your fit',     sub: 'Slim · Regular · Relaxed · Luxury Loose' },
          { Icon: Sparkles, label: 'Perfect measurements', sub: 'Your body + your fit → tailor cut' },
        ].map(({ Icon, label, sub }, i) => (
          <div key={i} className="flex items-center gap-3 px-3.5 py-2.5"
            style={{ background: '#FFF', border: `1px solid ${LINE}` }}>
            <div className="w-8 h-8 flex items-center justify-center"
              style={{ background: SOFT, border: `1px solid ${LINE}` }}>
              <Icon className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
            </div>
            <div>
              <p className="text-[12px]" style={{ color: INK, fontWeight: 500 }}>{label}</p>
              <p className="text-[10px]" style={{ color: MUTED }}>{sub}</p>
            </div>
            <span className="ml-auto text-[10px]" style={{ color: GOLD, fontWeight: 600 }}>0{i + 1}</span>
          </div>
        ))}
      </div>

      <button
        data-testid="welcome-start-btn" onClick={onContinue}
        className="w-full py-3.5 text-[12px] tracking-[0.22em] uppercase text-white flex items-center justify-center gap-2 active:scale-[0.98] transition"
        style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)', fontWeight: 500 }}>
        Begin My AI Fitting <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <div className="mt-auto pt-6 flex items-center justify-center gap-3 text-[9px]" style={{ color: MUTED }}>
        <span className="flex items-center gap-1">
          <ShieldCheck className="w-2.5 h-2.5" style={{ color: '#166534' }} strokeWidth={1.5} />
          Photos auto-deleted
        </span>
        <span>·</span>
        <span>Free Alterations</span>
        <span>·</span>
        <span>Master Tailor Verified</span>
      </div>
    </div>
  </div>
);

// ─── Step 1 — Height (with FT/IN ↔ CM toggle) ────────────────────────────────
// Pure utility conversions. Rounding happens ONLY on unit toggle or commit —
// never during typing. Typing is owned by local string state.
const heightToFtIn = (cm) => {
  const totalIn = cm / 2.54;
  const ft = Math.floor(totalIn / 12);
  const inch = Math.round(totalIn - ft * 12);
  if (inch === 12) return { ft: ft + 1, inch: 0 };
  return { ft, inch };
};
const ftInToCm = (ft, inch) => Math.round(((Number(ft) || 0) * 12 + (Number(inch) || 0)) * 2.54);
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Validation ranges (spec)
const CM_MIN = 90, CM_MAX = 250;
const FT_MIN = 3, FT_MAX = 8;
const IN_MIN = 0, IN_MAX = 11;

const HeightStep = ({ value, onChange, onContinue, onBack, schemaLabel }) => {
  const initCm = clamp(value.height_cm || 170, CM_MIN, CM_MAX);
  const initFtIn = heightToFtIn(initCm);

  // Unit mode — persisted in payload
  const [unit, setUnit] = useState(value.height_unit === 'ftin' ? 'ftin' : 'cm');

  // Local STRING state — the single source of truth for what the user sees.
  // Strings (not numbers) so "" and intermediate "1" don't get coerced to 0.
  const [cmStr, setCmStr] = useState(String(initCm));
  const [ftStr, setFtStr] = useState(String(initFtIn.ft));
  const [inStr, setInStr] = useState(String(initFtIn.inch));

  // Parsed integers
  const cmN = parseInt(cmStr, 10);
  const ftN = parseInt(ftStr, 10);
  const inN = parseInt(inStr, 10);

  // Validity per mode
  const validCm = !Number.isNaN(cmN) && cmN >= CM_MIN && cmN <= CM_MAX;
  const validFtIn =
    !Number.isNaN(ftN) && ftN >= FT_MIN && ftN <= FT_MAX &&
    !Number.isNaN(inN) && inN >= IN_MIN && inN <= IN_MAX;
  const valid = unit === 'cm' ? validCm : validFtIn;

  // Compute the cm value represented by current local strings (for commit + helper)
  const currentCm = unit === 'cm'
    ? (validCm ? cmN : initCm)
    : (validFtIn ? ftInToCm(ftN, inN) : initCm);

  // Helper text — ONLY a preview. Never mutates the input values.
  const helper = unit === 'cm'
    ? (validCm
        ? (() => { const { ft, inch } = heightToFtIn(cmN); return `≈ ${ft} ft ${inch} in`; })()
        : `Enter between ${CM_MIN} and ${CM_MAX} cm`)
    : (validFtIn
        ? `≈ ${ftInToCm(ftN, inN)} cm`
        : `Feet ${FT_MIN}–${FT_MAX} · Inches ${IN_MIN}–${IN_MAX}`);

  // Unit switch — recompute the OTHER mode's strings from the currently-valid value.
  const switchUnit = (next) => {
    if (next === unit) return;
    if (next === 'cm') {
      // coming from ftin → freshly derive cm
      const newCm = validFtIn ? ftInToCm(ftN, inN) : initCm;
      setCmStr(String(newCm));
    } else {
      // coming from cm → freshly derive ft/in
      const src = validCm ? cmN : initCm;
      const { ft, inch } = heightToFtIn(src);
      setFtStr(String(ft));
      setInStr(String(inch));
    }
    setUnit(next);
  };

  // Commit to parent (debounced via blur / continue). Never during typing.
  const commit = () => {
    if (!valid) return;
    if (currentCm !== value.height_cm || unit !== value.height_unit) {
      onChange({ ...value, height_cm: currentCm, height_unit: unit });
    }
  };

  // Sanitized onChange for numeric fields — strips non-digits, caps length.
  const digitsOnly = (s, max = 3) => s.replace(/[^0-9]/g, '').slice(0, max);

  const handleContinue = () => {
    if (!valid) return;
    commit();
    onContinue();
  };

  const GOLD_SHADOW = '0 6px 18px rgba(212,175,55,0.28)';

  return (
    <div data-testid="step-height" className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT }}>
      <StepHeader stepNumber={1} title="Your Height" schemaLabel={schemaLabel} onBack={onBack} />
      <div className="flex-1 px-5 py-7">
        <div className="flex items-center gap-2 mb-2">
          <Ruler className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
          <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: GOLD }}>Step 1 · Height</p>
        </div>
        <h2 className="mb-2"
          style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 28, fontWeight: 500, letterSpacing: '-0.01em' }}>
          How tall are you?
        </h2>
        <p className="text-[12px] mb-6" style={{ color: MUTED, lineHeight: 1.55 }}>
          Pick your preferred unit. Type manually — the other unit updates as a helper only.
        </p>

        {/* High-visibility unit toggle — two equal-width pills, gold active state */}
        <div data-testid="unit-toggle" className="flex gap-2 mb-7">
          {[
            { v: 'ftin', l: 'Ft / In' },
            { v: 'cm', l: 'Cm' },
          ].map(o => {
            const active = unit === o.v;
            return (
              <button
                key={o.v} data-testid={`unit-${o.v}`}
                onClick={() => switchUnit(o.v)}
                aria-pressed={active}
                className="flex-1 py-3 text-[12px] tracking-[0.22em] uppercase transition-all duration-300 active:scale-[0.98]"
                style={{
                  background: active ? GOLD : '#FFF',
                  color: active ? INK : MUTED,
                  border: `1px solid ${active ? GOLD : LINE}`,
                  fontWeight: active ? 700 : 500,
                  boxShadow: active ? GOLD_SHADOW : 'none',
                  letterSpacing: '0.22em',
                }}>
                {o.l}
              </button>
            );
          })}
        </div>

        {/* Manual-input first — large luxury typography, numeric keyboard */}
        {unit === 'cm' ? (
          <div data-testid="height-display-cm" className="mb-3">
            <label className="block mb-2 text-[10px] tracking-[0.22em] uppercase" style={{ color: MUTED }}>
              Height (cm)
            </label>
            <div className="flex items-baseline gap-3">
              <input
                data-testid="height-cm-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={cmStr}
                onChange={(e) => setCmStr(digitsOnly(e.target.value, 3))}
                onBlur={commit}
                autoComplete="off"
                placeholder="178"
                className="flex-1 text-left focus:outline-none"
                style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: 64,
                  fontWeight: 500,
                  color: validCm ? INK : '#991B1B',
                  borderBottom: `1.5px solid ${validCm ? GOLD : '#991B1B'}`,
                  background: 'transparent',
                  padding: '6px 4px',
                }} />
              <span className="text-[22px]" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>cm</span>
            </div>
          </div>
        ) : (
          <div data-testid="height-display-ftin" className="mb-3">
            <label className="block mb-2 text-[10px] tracking-[0.22em] uppercase" style={{ color: MUTED }}>
              Height (ft / in)
            </label>
            <div className="flex items-baseline gap-4">
              <div className="flex items-baseline gap-1 flex-1">
                <input
                  data-testid="height-ft-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={ftStr}
                  onChange={(e) => setFtStr(digitsOnly(e.target.value, 1))}
                  onBlur={commit}
                  autoComplete="off"
                  placeholder="5"
                  className="w-20 text-center focus:outline-none"
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: 64,
                    fontWeight: 500,
                    color: (!Number.isNaN(ftN) && ftN >= FT_MIN && ftN <= FT_MAX) ? INK : '#991B1B',
                    borderBottom: `1.5px solid ${(!Number.isNaN(ftN) && ftN >= FT_MIN && ftN <= FT_MAX) ? GOLD : '#991B1B'}`,
                    background: 'transparent',
                    padding: '6px 4px',
                  }} />
                <span className="text-[18px]" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>ft</span>
              </div>
              <div className="flex items-baseline gap-1 flex-1">
                <input
                  data-testid="height-in-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={inStr}
                  onChange={(e) => setInStr(digitsOnly(e.target.value, 2))}
                  onBlur={commit}
                  autoComplete="off"
                  placeholder="10"
                  className="w-20 text-center focus:outline-none"
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: 64,
                    fontWeight: 500,
                    color: (!Number.isNaN(inN) && inN >= IN_MIN && inN <= IN_MAX) ? INK : '#991B1B',
                    borderBottom: `1.5px solid ${(!Number.isNaN(inN) && inN >= IN_MIN && inN <= IN_MAX) ? GOLD : '#991B1B'}`,
                    background: 'transparent',
                    padding: '6px 4px',
                  }} />
                <span className="text-[18px]" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>in</span>
              </div>
            </div>
          </div>
        )}

        {/* Helper text — preview only, never mutates inputs */}
        <p data-testid="height-helper" className="text-[11px] mb-1"
          style={{ color: valid ? MUTED : '#991B1B', letterSpacing: '0.02em' }}>
          {helper}
        </p>
        <p className="text-[9.5px]" style={{ color: MUTED }}>
          Range: {unit === 'cm' ? `${CM_MIN}–${CM_MAX} cm` : `${FT_MIN}–${FT_MAX} ft · ${IN_MIN}–${IN_MAX} in`}
        </p>
      </div>
      <StepFooter onClick={handleContinue} disabled={!valid} testId="height-continue">
        Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </StepFooter>
    </div>
  );
};

// ─── Step 2 — Weight (with KG ↔ LB toggle, local-string state to avoid keystroke jumps) ───
const KG_MIN = 30, KG_MAX = 200;
const LB_MIN = 66, LB_MAX = 441;
const kgToLb = (kg) => Math.round(kg * 2.205);
const lbToKg = (lb) => Math.round(lb / 2.205);

const WeightStep = ({ value, onChange, onContinue, onBack, schemaLabel }) => {
  const initKg = clamp(value.weight_kg || 65, KG_MIN, KG_MAX);
  const [unit, setUnit] = useState(value.weight_unit === 'lb' ? 'lb' : 'kg');

  // Local string state — never round-trips through parent during typing.
  const [kgStr, setKgStr] = useState(String(initKg));
  const [lbStr, setLbStr] = useState(String(kgToLb(initKg)));

  const kgN = parseInt(kgStr, 10);
  const lbN = parseInt(lbStr, 10);

  const validKg = !Number.isNaN(kgN) && kgN >= KG_MIN && kgN <= KG_MAX;
  const validLb = !Number.isNaN(lbN) && lbN >= LB_MIN && lbN <= LB_MAX;
  const valid = unit === 'kg' ? validKg : validLb;

  // The cm/kg representation used for commit + helper text.
  const currentKg = unit === 'kg'
    ? (validKg ? kgN : initKg)
    : (validLb ? lbToKg(lbN) : initKg);

  const helper = unit === 'kg'
    ? (validKg ? `≈ ${kgToLb(kgN)} lb` : `Enter between ${KG_MIN} and ${KG_MAX} kg`)
    : (validLb ? `≈ ${lbToKg(lbN)} kg` : `Enter between ${LB_MIN} and ${LB_MAX} lb`);

  const switchUnit = (next) => {
    if (next === unit) return;
    if (next === 'kg') {
      setKgStr(String(validLb ? lbToKg(lbN) : initKg));
    } else {
      setLbStr(String(validKg ? kgToLb(kgN) : kgToLb(initKg)));
    }
    setUnit(next);
  };

  const commit = () => {
    if (!valid) return;
    if (currentKg !== value.weight_kg || unit !== value.weight_unit) {
      onChange({ ...value, weight_kg: currentKg, weight_unit: unit });
    }
  };

  const digitsOnly = (s, max = 3) => s.replace(/[^0-9]/g, '').slice(0, max);

  const handleContinue = () => {
    if (!valid) return;
    commit();
    onContinue();
  };

  const GOLD_SHADOW = '0 6px 18px rgba(212,175,55,0.28)';

  return (
    <div data-testid="step-weight" className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT }}>
      <StepHeader stepNumber={2} title="Your Weight" schemaLabel={schemaLabel} onBack={onBack} />
      <div className="flex-1 px-5 py-7">
        <div className="flex items-center gap-2 mb-2">
          <Scale className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
          <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: GOLD }}>Step 2 · Weight</p>
        </div>
        <h2 className="mb-2"
          style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 28, fontWeight: 500, letterSpacing: '-0.01em' }}>
          And your weight?
        </h2>
        <p className="text-[12px] mb-6" style={{ color: MUTED, lineHeight: 1.55 }}>
          Pick your preferred unit. Type manually — the other unit updates as a helper only.
        </p>

        {/* High-visibility unit toggle */}
        <div data-testid="weight-unit-toggle" className="flex gap-2 mb-7">
          {[{ v: 'kg', l: 'Kg' }, { v: 'lb', l: 'Lb' }].map(o => {
            const active = unit === o.v;
            return (
              <button key={o.v} data-testid={`weight-unit-${o.v}`}
                onClick={() => switchUnit(o.v)} aria-pressed={active}
                className="flex-1 py-3 text-[12px] tracking-[0.22em] uppercase transition-all duration-300 active:scale-[0.98]"
                style={{
                  background: active ? GOLD : '#FFF',
                  color: active ? INK : MUTED,
                  border: `1px solid ${active ? GOLD : LINE}`,
                  fontWeight: active ? 700 : 500,
                  boxShadow: active ? GOLD_SHADOW : 'none',
                  letterSpacing: '0.22em',
                }}>
                {o.l}
              </button>
            );
          })}
        </div>

        {/* Manual-input first */}
        {unit === 'kg' ? (
          <div className="mb-3">
            <label className="block mb-2 text-[10px] tracking-[0.22em] uppercase" style={{ color: MUTED }}>
              Weight (kg)
            </label>
            <div className="flex items-baseline gap-3">
              <input
                data-testid="weight-input"
                type="text" inputMode="numeric" pattern="[0-9]*"
                value={kgStr}
                onChange={(e) => setKgStr(digitsOnly(e.target.value, 3))}
                onBlur={commit}
                autoComplete="off" placeholder="65"
                className="flex-1 text-left focus:outline-none"
                style={{
                  fontFamily: 'Playfair Display, serif', fontSize: 64, fontWeight: 500,
                  color: validKg ? INK : '#991B1B',
                  borderBottom: `1.5px solid ${validKg ? GOLD : '#991B1B'}`,
                  background: 'transparent', padding: '6px 4px',
                }} />
              <span className="text-[22px]" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>kg</span>
            </div>
          </div>
        ) : (
          <div className="mb-3">
            <label className="block mb-2 text-[10px] tracking-[0.22em] uppercase" style={{ color: MUTED }}>
              Weight (lb)
            </label>
            <div className="flex items-baseline gap-3">
              <input
                data-testid="weight-input"
                type="text" inputMode="numeric" pattern="[0-9]*"
                value={lbStr}
                onChange={(e) => setLbStr(digitsOnly(e.target.value, 3))}
                onBlur={commit}
                autoComplete="off" placeholder="143"
                className="flex-1 text-left focus:outline-none"
                style={{
                  fontFamily: 'Playfair Display, serif', fontSize: 64, fontWeight: 500,
                  color: validLb ? INK : '#991B1B',
                  borderBottom: `1.5px solid ${validLb ? GOLD : '#991B1B'}`,
                  background: 'transparent', padding: '6px 4px',
                }} />
              <span className="text-[22px]" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>lb</span>
            </div>
          </div>
        )}

        <p data-testid="weight-helper" className="text-[11px] mb-1"
          style={{ color: valid ? MUTED : '#991B1B', letterSpacing: '0.02em' }}>
          {helper}
        </p>
        <p className="text-[9.5px]" style={{ color: MUTED }}>
          Range: {unit === 'kg' ? `${KG_MIN}–${KG_MAX} kg` : `${LB_MIN}–${LB_MAX} lb`}
        </p>
      </div>
      <StepFooter onClick={handleContinue} disabled={!valid} testId="weight-continue">
        Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </StepFooter>
    </div>
  );
};

// ─── Step 3 — Style Profile (Fit + Padded + Age — all in one elegant card) ───
//
// Replaces the previous two-step "Usual Size" + "Fit Preference" pair with a
// single luxury form. AI Fusion does the heavy tailoring inference; we only
// ask for soft signals that demonstrably improve fit:
//   · fit preference (drape silhouette)
//   · padded vs non-padded  (cup allowance + dart behavior — women's tops only)
//   · age range  (optional — soft posture/proportion hint for AI)
const StyleStep = ({ value, onChange, onContinue, onBack, schemaKey, schemaLabel, gender }) => {
  const showPadded = gender === 'female' && PADDED_SCHEMAS.has(schemaKey);
  const setFit = (opt) => onChange({ ...value, fit_pref: opt.backend, fit_label: opt.ui });
  const setPadded = (p) => onChange({ ...value, padded: p });
  const setAge = (v) => onChange({ ...value, age_range: value.age_range === v ? null : v });

  return (
    <div data-testid="step-style" className="h-[100dvh] flex flex-col" style={{ backgroundColor: SOFT }}>
      <StepHeader stepNumber={3} title="Style Profile" schemaLabel={schemaLabel} onBack={onBack} />
      <div data-testid="step-style-scroll" className="flex-1 min-h-0 overflow-y-auto px-5 py-7">
        <p className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: GOLD }}>Step 3 · Style</p>
        <h2 className="mb-2"
          style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 28, fontWeight: 500, letterSpacing: '-0.01em' }}>
          A few style signals
        </h2>
        <p className="text-[12px] mb-7" style={{ color: MUTED, lineHeight: 1.55 }}>
          Tell us how you like it to sit. Our AI handles the rest.
        </p>

        {/* Fit preference */}
        <p className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: MUTED, fontWeight: 600 }}>
          Fit Preference
        </p>
        <div className="space-y-2 mb-6">
          {FIT_OPTIONS.map(o => {
            const selected = value.fit_label === o.ui;
            return (
              <button key={o.ui} data-testid={`fit-${o.ui}`} onClick={() => setFit(o)}
                className="w-full flex items-center justify-between px-4 py-3 text-left transition active:scale-[0.99]"
                style={{
                  background: selected ? INK : '#FFF',
                  color: selected ? '#FFF' : INK,
                  border: `1px solid ${selected ? INK : LINE}`,
                }}>
                <div>
                  <p className="text-[14px]"
                    style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}>
                    {o.label}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: selected ? 'rgba(255,255,255,0.7)' : MUTED }}>
                    {o.desc}
                  </p>
                </div>
                {selected && (
                  <span className="text-[9.5px] tracking-[0.18em] uppercase"
                    style={{ color: GOLD, fontWeight: 600 }}>Selected</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Padded — only for female upper-body schemas */}
        {showPadded && (
          <>
            <p className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: MUTED, fontWeight: 600 }}>
              Inner Padding
            </p>
            <p className="text-[10.5px] mb-2" style={{ color: MUTED, lineHeight: 1.5 }}>
              Affects cup allowance and dart shaping. Skip if unsure.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-6" data-testid="padded-toggle">
              {[
                { v: true,  l: 'Padded',     sub: 'Cup support · structured shape' },
                { v: false, l: 'Non-Padded', sub: 'Soft drape · natural silhouette' },
              ].map(o => {
                const sel = value.padded === o.v;
                return (
                  <button key={String(o.v)}
                    data-testid={`padded-${o.v ? 'yes' : 'no'}`}
                    onClick={() => setPadded(o.v)}
                    className="px-3 py-3 text-left transition active:scale-[0.98]"
                    style={{
                      background: sel ? INK : '#FFF',
                      color: sel ? '#FFF' : INK,
                      border: `1px solid ${sel ? INK : LINE}`,
                    }}>
                    <p className="text-[13px]" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}>
                      {o.l}
                    </p>
                    <p className="text-[9.5px] mt-0.5" style={{ color: sel ? 'rgba(255,255,255,0.7)' : MUTED, lineHeight: 1.4 }}>
                      {o.sub}
                    </p>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Age range — optional soft signal */}
        <p className="text-[10px] tracking-[0.18em] uppercase mb-1" style={{ color: MUTED, fontWeight: 600 }}>
          Age Range <span className="text-[9px]" style={{ color: GOLD, letterSpacing: '0.10em' }}>· Optional</span>
        </p>
        <p className="text-[10.5px] mb-2" style={{ color: MUTED, lineHeight: 1.5 }}>
          Helps the AI fine-tune posture and proportions.
        </p>
        <div className="grid grid-cols-4 gap-2" data-testid="age-range">
          {AGE_RANGES.map(o => {
            const sel = value.age_range === o.v;
            return (
              <button key={o.v}
                data-testid={`age-${o.v}`}
                onClick={() => setAge(o.v)}
                className="py-2.5 text-[11px] tracking-[0.04em] transition active:scale-[0.97]"
                style={{
                  background: sel ? GOLD : '#FFF',
                  color: sel ? INK : MUTED,
                  border: `1px solid ${sel ? GOLD : LINE}`,
                  fontWeight: sel ? 600 : 500,
                }}>
                {o.l}
              </button>
            );
          })}
        </div>
      </div>
      <StepFooter onClick={onContinue} disabled={!value.fit_pref} testId="style-continue">
        Next: Body Scan <Camera className="w-4 h-4" strokeWidth={1.5} />
      </StepFooter>
    </div>
  );
};

// ─── Step: Body Scan (Lunora's AI Body Scan) ─────────────────────────────────
//
// This is the ONLY body-measurement action in the customer journey. It powers
// the entire fit pipeline. The underlying scan provider is never named in the
// customer UI — this is a white-label Lunora's experience.
const ScanStep = ({ schemaLabel, onComplete, onBack }) => (
  <div data-testid="step-scan" className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT }}>
    <StepHeader stepNumber={1} title="AI Body Scan" schemaLabel={schemaLabel} onBack={onBack} />
    <div className="flex-1 px-5 py-7 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <Camera className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
        <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: GOLD }}>
          Step 1 · AI Body Scan
        </p>
      </div>
      <h2 className="mb-2"
        style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 28, fontWeight: 500, letterSpacing: '-0.01em' }}>
        Capture your measurements
      </h2>
      <p className="text-[12px] mb-6" style={{ color: MUTED, lineHeight: 1.55 }}>
        Launch the Lunora&apos;s AI Body Scan. Follow the on-screen guidance —
        the scan captures your body securely and returns couture-grade measurements
        for your {schemaLabel} fit.
      </p>

      <div className="mb-6 space-y-2">
        {[
          { Icon: ShieldCheck, label: 'Private & secure', sub: 'Scan runs in-browser · photos never stored' },
          { Icon: Wand2,       label: 'Master-tailor grade', sub: 'Real measurement engine · no estimates' },
          { Icon: Sparkles,    label: 'Reusable profile', sub: 'One scan powers every future order' },
        ].map(({ Icon, label, sub }, i) => (
          <div key={i} className="flex items-center gap-3 px-3.5 py-2.5"
            style={{ background: '#FFF', border: `1px solid ${LINE}` }}>
            <div className="w-8 h-8 flex items-center justify-center"
              style={{ background: SOFT, border: `1px solid ${LINE}` }}>
              <Icon className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
            </div>
            <div>
              <p className="text-[12px]" style={{ color: INK, fontWeight: 500 }}>{label}</p>
              <p className="text-[10px]" style={{ color: MUTED }}>{sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-auto">
        <StartBodyScanButton
          testId="stylist-flow-start-scan"
          onComplete={onComplete}
        />
        <p className="text-[9.5px] text-center mt-3" style={{ color: MUTED }}>
          By continuing you agree to our secure scan processing.
        </p>
      </div>
    </div>
  </div>
);

// ─── Step: Post-Scan Fit Preference ──────────────────────────────────────────
//
// Shown ONCE the scan has produced authoritative body measurements. Selecting a
// fit here re-runs the Fit Fusion Engine with the scanned body + chosen ease.
const PostScanFitStep = ({ value, onChange, onContinue, onBack, result, schemaLabel, schemaKey, gender }) => {
  const showPadded = gender === 'female' && PADDED_SCHEMAS.has(schemaKey);
  const setFit = (opt) => onChange({ ...value, fit_pref: opt.backend, fit_label: opt.ui });
  const setPadded = (p) => onChange({ ...value, padded: p });
  const bodyChest = result?.body?.chest ?? result?.body?.bust;

  return (
    <div data-testid="step-fit-pref" className="h-[100dvh] flex flex-col" style={{ backgroundColor: SOFT }}>
      <StepHeader stepNumber={2} title="Fit Preference" schemaLabel={schemaLabel} onBack={onBack} />
      <div data-testid="step-fit-pref-scroll" className="flex-1 min-h-0 overflow-y-auto px-5 py-6">
        {/* Success banner — the transition after successful scan */}
        <div data-testid="scan-success-banner"
          className="mb-6 px-4 py-3.5"
          style={{
            background: 'linear-gradient(135deg, rgba(22,101,52,0.06), rgba(212,175,55,0.06))',
            border: '1px solid rgba(22,101,52,0.30)',
          }}>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#166534' }} strokeWidth={1.6} />
            <p className="text-[10.5px] tracking-[0.18em] uppercase" style={{ color: '#166534', fontWeight: 600 }}>
              Your measurements are ready
            </p>
          </div>
          <p className="text-[11.5px]" style={{ color: MUTED, lineHeight: 1.55 }}>
            Your body profile has been captured.
            {typeof bodyChest === 'number' && (
              <> Recommended size <b style={{ color: INK }}>{result?.suggested_size}</b> based on your measurements.</>
            )}
          </p>
        </div>

        <p className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: GOLD }}>Step 2 · Fit</p>
        <h2 className="mb-1"
          style={{ fontFamily: 'Playfair Display, serif', color: INK, fontSize: 28, fontWeight: 500, letterSpacing: '-0.01em' }}>
          How should your outfit fit?
        </h2>
        <p className="text-[12px] mb-6" style={{ color: MUTED, lineHeight: 1.55 }}>
          Choose a silhouette. We apply the ease on top of your scanned measurements.
        </p>

        <div className="space-y-2 mb-6">
          {FIT_OPTIONS.map(o => {
            const selected = value.fit_label === o.ui;
            return (
              <button key={o.ui} data-testid={`fit-${o.ui}`} onClick={() => setFit(o)}
                className="w-full flex items-center justify-between px-4 py-3.5 text-left transition active:scale-[0.99]"
                style={{
                  background: selected ? INK : '#FFF',
                  color: selected ? '#FFF' : INK,
                  border: `1px solid ${selected ? INK : LINE}`,
                }}>
                <div>
                  <p className="text-[14px]"
                    style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}>
                    {o.label}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: selected ? 'rgba(255,255,255,0.7)' : MUTED }}>
                    {o.desc}
                  </p>
                </div>
                {selected && (
                  <span className="text-[9.5px] tracking-[0.18em] uppercase"
                    style={{ color: GOLD, fontWeight: 600 }}>Selected</span>
                )}
              </button>
            );
          })}
        </div>

        {showPadded && (
          <>
            <p className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: MUTED, fontWeight: 600 }}>
              Inner Padding <span className="text-[9px]" style={{ color: GOLD, letterSpacing: '0.10em' }}>· Optional</span>
            </p>
            <p className="text-[10.5px] mb-2" style={{ color: MUTED, lineHeight: 1.5 }}>
              Affects cup allowance and dart shaping.
            </p>
            <div className="grid grid-cols-2 gap-2" data-testid="padded-toggle">
              {[
                { v: true,  l: 'Padded',     sub: 'Cup support · structured shape' },
                { v: false, l: 'Non-Padded', sub: 'Soft drape · natural silhouette' },
              ].map(o => {
                const sel = value.padded === o.v;
                return (
                  <button key={String(o.v)}
                    data-testid={`padded-${o.v ? 'yes' : 'no'}`}
                    onClick={() => setPadded(o.v)}
                    className="px-3 py-3 text-left transition active:scale-[0.98]"
                    style={{
                      background: sel ? INK : '#FFF',
                      color: sel ? '#FFF' : INK,
                      border: `1px solid ${sel ? INK : LINE}`,
                    }}>
                    <p className="text-[13px]" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}>
                      {o.l}
                    </p>
                    <p className="text-[9.5px] mt-0.5" style={{ color: sel ? 'rgba(255,255,255,0.7)' : MUTED, lineHeight: 1.4 }}>
                      {o.sub}
                    </p>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <StepFooter onClick={onContinue} disabled={!value.fit_pref} testId="fit-pref-continue">
        Review My Fit <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </StepFooter>
    </div>
  );
};

// ─── Main Flow Orchestrator ──────────────────────────────────────────────────
const StylistFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  // Re-validate the session on entry so an expired login is not trusted.
  useEffect(() => { checkAuth(); }, [checkAuth]);
  // Garment context survives the 3DLOOK redirect (which drops router state).
  const orderData = useMemo(() => resolveOrderContext(location.state), [location.state]);
  const hasCategory = Boolean(orderData.category);
  const category = orderData.category || 'kurta_pajama';
  const schemaKey = CATEGORY_TO_SCHEMA[category] || 'kurta_pajama';
  const schemaLabel = SCHEMA_LABELS[schemaKey];

  const inferredGender = useMemo(() => {
    if (orderData.gender) return orderData.gender === 'women' ? 'female' : orderData.gender === 'men' ? 'male' : 'female';
    if (['blouse', 'suit'].includes(schemaKey)) return 'female';
    if (['kurta_pajama', 'men_shirt', 'sherwani'].includes(schemaKey)) return 'male';
    return 'female';
  }, [orderData.gender, schemaKey]);

  const [payload, setPayload] = useState(() => {
    let saved = {};
    try {
      const raw = localStorage.getItem(QUICKSTART_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch { /* ignore */ }
    return {
      height_cm: 170, weight_kg: 65,
      // usual_size kept as a sensible default for backend compatibility ('M') —
      // the chip step that asked for it has been removed from the customer flow.
      usual_size: 'M',
      fit_pref: 'regular', fit_label: 'regular',
      padded: null,         // null = unset (women's upper-body only)
      age_range: null,      // null = optional / unset
      height_unit: 'cm', weight_unit: 'kg',
      ...saved,
      // Schema-derived values must always win to prevent stale-cache bugs.
      gender: inferredGender,
      answers: orderData.answers || saved.answers || DEFAULT_ANSWERS[schemaKey] || {},
    };
  });

  const persist = (next) => {
    try {
      const { answers, ...quick } = next;
      localStorage.setItem(QUICKSTART_KEY, JSON.stringify({ ...quick, answers }));
    } catch { /* ignore */ }
  };
  const updatePayload = (n) => { setPayload(n); persist(n); };

  // Linear step machine
  const [step, setStep] = useState(location.state?.resumeStep === 'scan' ? 'scan' : 'welcome');
  const [result, setResult] = useState(null);
  // Recovery state so we can show a "Restoring your scan…" placeholder
  // instead of the Welcome hero while we reconcile with the backend.
  const [recovering, setRecovering] = useState(true);

  const goNext = (next) => {
    // Keep scan step in history state so a reload / re-login resumes it.
    if (next === 'scan' || next === 'welcome') {
      navigate(location.pathname + location.search, { replace: true, state: { ...(location.state || {}), resumeStep: next } });
    }
    setStep(next);
  };

  // ─── Build EditableReview-shaped `result` from a persisted body_profile ──
  // Also computes the initial GARMENT (cutting) measurements by running the
  // scanned body through the Fit Fusion Engine. Body values are preserved
  // separately so the Review screen can show BODY vs GARMENT as distinct
  // sections.
  const buildResultFromProfile = async (profile) => {
    if (!profile) return null;
    const measurements = profile.measurements || {};
    // suggested_size will be recomputed by the engine from the actual body,
    // this is just a placeholder for the anonymous / no-scan edge.
    let suggested_size = payload.usual_size || 'M';
    const heightM  = ((profile.height_cm ?? payload.height_cm) || 170) / 100;
    const weightKg =   profile.weight_kg ?? payload.weight_kg ?? 65;
    const bmi = Math.round((weightKg / (heightM * heightM)) * 10) / 10;

    // Prefer 3DLOOK's height/weight when the scan has them (architecture rule #10).
    const scan_height_cm = profile.height_cm ?? payload.height_cm;
    const scan_weight_kg = profile.weight_kg ?? payload.weight_kg;
    const scan_gender    = profile.gender    || payload.gender || 'female';

    // Run the fusion engine so we have GARMENT measurements ready for display
    // on the very first Review render. If the engine call fails, fall back to
    // showing just the body — the UI degrades gracefully.
    let engine = null;
    try {
      engine = await measurementsAPI.fuse({
        category: schemaKey, gender: scan_gender,
        height_cm: scan_height_cm, weight_kg: scan_weight_kg,
        // usual_size is a legacy fallback — the engine will ignore it when
        // body_measurements is supplied. We still send M for anonymous back-compat.
        usual_size: payload.usual_size || 'M',
        fit_pref: payload.fit_pref || 'regular',
        answers: payload.answers || {},
        body_measurements: measurements,
      });
    } catch { /* fall back to body-only display below */ }

    return {
      body: measurements,                                  // canonical map fed to the engine
      provider_measurements: profile.provider_measurements, // every 3DLOOK measurement, verbatim
      derived: engine?.derived || measurements,            // garment cut (or body fallback)
      suggested_size: engine?.suggested_size || suggested_size,
      fit_confidence: engine?.fit_confidence
                      ?? (profile?.confidence?.overall ?? 92),
      ease_applied: engine?.ease_applied || {},
      derived_provenance: engine?.derived_provenance || {},
      bmi: engine?.bmi ?? bmi,
      height_cm: scan_height_cm,
      weight_kg: scan_weight_kg,
      gender: scan_gender,
      source: engine?.source || (profile.source || '3dlook_api'),
      engine_source: engine?.source,
      saia_person_id: profile.saia_person_id,
      scan_date: profile.scan_date,
    };
  };

  // ─── 3-layer post-scan recovery on mount ──────────────────────────────
  // A) URL params from 3DLOOK's returnUrl (?person / ?person_id / ?taskset)
  // B) Server-side active body profile
  // C) Pending scan-session token → SAIA person listing recovery
  useEffect(() => {
    let cancelled = false;

    const readUrlHint = () => {
      try {
        const qs = new URLSearchParams(location.search || '');
        const hashStr = (window.location.hash || '').replace(/^#/, '');
        const hs = new URLSearchParams(hashStr);
        // Kate confirmed (2026-09-11): 3DLOOK's returnUrl carries EXACTLY
        //   ?measurements=true&id=<PERSON_ID>&uuid=<WIDGET_FLOW_ID>
        // Do NOT rename these to `person` / `taskset` — those keys never appear.
        const pid = qs.get('id') || hs.get('id');
        const uid = qs.get('uuid') || hs.get('uuid');
        return pid ? { person_id: pid, taskset_id: uid || undefined } : null;
      } catch { return null; }
    };

    const stripUrlHints = () => {
      try {
        const url = new URL(window.location.href);
        ['id', 'uuid', 'measurements'].forEach(k => url.searchParams.delete(k));
        if (window.location.hash) {
          const hashStr = window.location.hash.replace(/^#/, '');
          const hs = new URLSearchParams(hashStr);
          ['id', 'uuid', 'measurements'].forEach(k => hs.delete(k));
          const remain = hs.toString();
          url.hash = remain ? `#${remain}` : '';
        }
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore */ }
    };

    const readSessionToken = () => {
      try {
        const raw = localStorage.getItem('lunora_scan_session_v1');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.session_token) return null;
        // 1h TTL client-side too; backend enforces its own.
        if (Date.now() - (parsed.ts || 0) > 60 * 60 * 1000) return null;
        return parsed.session_token;
      } catch { return null; }
    };
    const PENDING_HINT_KEY = 'lunora_pending_scan_hint_v1';
    const readPendingHint = () => {
      try {
        const p = JSON.parse(localStorage.getItem(PENDING_HINT_KEY) || 'null');
        return p?.person_id && Date.now() - (p.ts || 0) < 60 * 60 * 1000 ? p : null;
      } catch { return null; }
    };
    const savePendingHint = (h) => {
      try { localStorage.setItem(PENDING_HINT_KEY, JSON.stringify({ person_id: h.person_id, taskset_id: h.taskset_id, ts: Date.now() })); } catch {/* noop */}
    };
    const clearPendingHint = () => {
      try { localStorage.removeItem(PENDING_HINT_KEY); } catch {/* noop */}
    };
    const clearSessionToken = () => {
      try { localStorage.removeItem('lunora_scan_session_v1'); } catch {/* noop */}
    };

    (async () => {
      // ── Layer A: URL param hint from 3DLOOK returnUrl ──
      // The auth cookie is often stripped by Chrome's 3rd-party-cookie /
      // Partitioned-cookie policy on the mtm-widget.3dlook.me → lunoras.in
      // return redirect (verified in prod 2026-09-30 forensic on run 97c8919e:
      // every /auth/* and /body-profile/* returned 401 immediately after the
      // scan). We therefore attach the server-side scan session_token from
      // localStorage as an auth alternative; the backend accepts it as the
      // cross-context-safe authentication mechanism in /recover-scan.
      const hint = readUrlHint() || readPendingHint();
      if (hint) {
        stripUrlHints();
        try {
          const profile = await ingestWithRetry(() => bodyProfileAPI.recoverScan({
            hint_person_id: hint.person_id,
            hint_taskset_id: hint.taskset_id,
            session_token: readSessionToken() || undefined,
          }));
          if (cancelled) return;
          clearSessionToken();
          clearPendingHint();
          const r = await buildResultFromProfile(profile);
          if (cancelled) return;
          setResult(r);
          setStep('review');
          setRecovering(false);
          return;
        } catch (e) {
          // Still processing → remember this exact person_id so the next visit retries it; else forget it.
          if (e?.code === 'SCAN_STILL_PROCESSING') savePendingHint(hint); else clearPendingHint();
          // Fall through to Layer B / C. Do NOT block the user.
          console.warn('[StylistFlow] URL-hint recovery failed:', e?.message);
        }
      }

      // ── Layer B: already-active saved profile (any prior scan) ──
      try {
        const { profile } = await bodyProfileAPI.me();
        if (cancelled) return;
        if (profile && profile.is_active && profile.status === 'completed'
            && profile.measurements && Object.keys(profile.measurements).length) {
          clearSessionToken();
          const r = await buildResultFromProfile(profile);
          if (cancelled) return;
          setResult(r);
          setStep('review');
          setRecovering(false);
          return;
        }
      } catch {/* not logged-in / no profile — normal for new customers */}

      // ── Layer C: pending scan-session token → ONLY if token exists ──
      // We NEVER auto-claim by time-window (SAIA trial plan cannot filter by
      // created_gte, so any auto-pick is unsafe). Recovery here only succeeds
      // if the widget callback fired but our page never got the person_id —
      // in that case /recover-scan will still 404 today, and we simply fall
      // back to Welcome. The customer can re-open the scan modal to retry.
      const token = readSessionToken();
      if (token) {
        try {
          const profile = await bodyProfileAPI.recoverScan({ session_token: token });
          if (cancelled) return;
          clearSessionToken();
          const r = await buildResultFromProfile(profile);
          if (cancelled) return;
          setResult(r);
          setStep('review');
          setRecovering(false);
          return;
        } catch (e) {
          const msg = e?.message || '';
          if (!/still computing|processing/i.test(msg)) clearSessionToken();
          console.info('[StylistFlow] scan-session recovery: nothing to restore.', msg);
        }
      }

      if (!cancelled) setRecovering(false);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── 3DLOOK widget completion ─────────────────────────────────────────────
  // StartBodyScanButton has already POSTed to /api/body-profile and returns the
  // saved profile document. We run it through buildResultFromProfile so the
  // Review screen displays BODY + GARMENT with a single consistent code path.
  const handleScanComplete = async (profile) => {
    const r = await buildResultFromProfile(profile);
    if (r) {
      setResult(r);
    }
    // After a successful scan, prompt for fit preference — the Review screen
    // then follows once the customer picks Slim / Regular / Relaxed / Luxury Loose.
    setStep(r ? 'fitPref' : 'review');
  };

  const continueToCheckout = ({ measurements, body_measurements, ease_applied, measurement_provenance, suggested_size, fit_confidence, schema }) => {
    navigate('/checkout', {
      state: {
        ...orderData,
        measurements,                              // GARMENT — for tailor cutting
        body_measurements: body_measurements || null,  // BODY — 3DLOOK audit trail
        ease_applied: ease_applied || null,
        measurement_provenance: measurement_provenance || null,
        measurementSchema: schema,
        suggestedSize: suggested_size,
        fitConfidence: fit_confidence,
      },
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  // Hooks above still run (scan recovery keeps saving the scan); we only refuse to
  // continue into garment measurements/checkout without a chosen garment.
  if (!hasCategory) {
    return (
      <div data-testid="garment-required" className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ background: SOFT }}>
        <p className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: GOLD }}>Choose your garment first</p>
        <p className="text-[13px] mb-5 max-w-xs" style={{ color: INK }}>
          Your body scan is saved to your fit profile. Select the garment you want stitched to see its cutting measurements.
        </p>
        <button data-testid="garment-required-select-btn" onClick={() => navigate('/gender-selection')}
          className="px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)' }}>
          Select a garment
        </button>
      </div>
    );
  }

  if (recovering) {
    return (
      <div data-testid="stylist-recovering" className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ background: SOFT }}>
        <Loader2 className="w-6 h-6 animate-spin mb-3" style={{ color: GOLD }} strokeWidth={1.4} />
        <p className="text-[10px] tracking-[0.22em] uppercase mb-1" style={{ color: GOLD }}>
          Restoring your fit profile
        </p>
        <p className="text-[13px]" style={{ color: MUTED, fontFamily: 'Playfair Display, serif' }}>
          One moment…
        </p>
      </div>
    );
  }
  if ((step === 'welcome' || step === 'scan') && user === false) {
    return <Navigate to="/auth" replace state={{ redirectTo: '/measurements',
      redirectState: { ...(location.state || {}), resumeStep: step } }} />;
  }
  if (step === 'welcome') {
    return <WelcomeHero schemaLabel={schemaLabel} userName={user?.name}
      onContinue={() => goNext('scan')} />;
  }
  if (step === 'scan') {
    return <ScanStep
      schemaLabel={schemaLabel}
      onComplete={handleScanComplete}
      onBack={() => goNext('welcome')} />;
  }
  if (step === 'fitPref' && result) {
    return <PostScanFitStep
      value={payload}
      onChange={updatePayload}
      result={result}
      schemaLabel={schemaLabel}
      schemaKey={schemaKey}
      gender={inferredGender}
      onContinue={() => goNext('review')}
      onBack={() => goNext('scan')} />;
  }
  if (step === 'review' && result) {
    return <EditableReview
      result={result} payload={payload} schemaKey={schemaKey} schemaLabel={schemaLabel}
      photoCount={0}
      source="ai_body_scan"
      onAdjustFit={(f) => updatePayload({ ...payload, fit_pref: f })}
      onContinue={continueToCheckout}
      onBack={() => goNext('fitPref')} />;
  }
  // Edge: if review reached but no result (scan error), show a graceful retry prompt.
  if (step === 'review' && !result) {
    return (
      <div data-testid="scan-error-fallback" className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ background: SOFT }}>
        <Sparkles className="w-8 h-8 mb-3" style={{ color: GOLD }} strokeWidth={1.4} />
        <p className="text-[16px] mb-1" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
          Couldn&apos;t finish the body scan
        </p>
        <p className="text-[11px] mb-5 text-center" style={{ color: MUTED }}>
          Please check your connection and try again.
        </p>
        <button data-testid="scan-retry-cta" onClick={() => goNext('scan')}
          className="px-6 py-2.5 text-[11px] tracking-[0.18em] uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #1A1A1A, #D4AF37 140%)', fontWeight: 500 }}>
          Retry
        </button>
      </div>
    );
  }
  return null;
};

export default StylistFlow;
